import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { isTransient } from "@/lib/api";
import { useAuthActions, canWithRole } from "@/hooks/useAuthActions";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWorkspaceId] = useState(null);
  const [ready, setReady] = useState(false);

  const clear = useCallback(() => { setUser(null); setWorkspaces([]); setActiveWorkspaceId(null); }, []);
  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setWorkspaces(data.workspaces || []);
      setActiveWorkspaceId(data.active_workspace_id || data.workspaces?.[0]?.id || null);
    } catch (err) {
      // Only a definitive auth answer (401/403) logs the user out; a restarting backend (5xx / network) keeps the current session.
      if (!isTransient(err)) setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // If returning from Google OAuth, AuthCallback exchanges the session first; skip /me here.
    if (window.location.hash?.includes("session_id=")) { setReady(true); return; }
    loadMe();
    window.addEventListener("sf:unauthorized", clear);
    return () => window.removeEventListener("sf:unauthorized", clear);
  }, [loadMe, clear]);

  const actions = useAuthActions({ loadMe, clear, setActiveWorkspaceId });

  const value = useMemo(() => {
    const currentWorkspace = workspaces.find((w) => w.id === activeWorkspaceId);
    const currentRole = currentWorkspace?.role || "readonly";
    return { user, workspaces, activeWorkspaceId, currentWorkspace, currentRole, ready, ...actions, can: canWithRole(currentRole), reload: loadMe };
  }, [user, workspaces, activeWorkspaceId, ready, loadMe, actions]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
